import {
  FUN_PAYMENT_STATES,
  FUN_PAYMENT_STEPS,
  type FunActivityResponse,
  type FunLookupResponse,
  type FunPaymentDelivery,
  type FunPaymentItem,
  type FunPaymentObservedResponse,
  type FunPaymentStartResponse,
  type FunPaymentState,
  isFinalFunPaymentState,
  XCHAIN_STAGES,
} from "@decibeltrade/utils";
import z from "zod/v4";

// Mirror of the server's Fun payment schemas; both satisfy the shared types in @decibeltrade/utils.

const FunPaymentDeliverySchema = z.object({
  id: z.string(),
  destinationAssetKey: z.string(),
  stage: z.enum(XCHAIN_STAGES),
  status: z.string(),
  updatedAt: z.string().nullable(),
  amountOutFormatted: z.string().nullable(),
  amountOutUsd: z.string().nullable(),
  withdrawFeeFormatted: z.string().nullable(),
  withdrawFeeUsd: z.string().nullable(),
  destinationTxHash: z.string().nullable(),
  destinationSymbol: z.string(),
  destinationFaAddress: z.string(),
  destinationDecimals: z.number(),
  sweepable: z.boolean(),
}) satisfies z.ZodType<FunPaymentDelivery>;

const KNOWN_STATES: ReadonlySet<string> = new Set(FUN_PAYMENT_STATES);
const isKnownState = (state: string): state is FunPaymentState => KNOWN_STATES.has(state);

const FunPaymentStepSchema = z.enum(FUN_PAYMENT_STEPS);
const stepView = z.object({
  status: z.enum(["done", "active", "pending", "failed"]),
  at: z.string().nullable(),
});

export const FunPaymentItemSchema = z.object({
  id: z.string(),
  aliases: z.array(z.string()),
  entry: z.enum(["card", "crypto"]).nullable(),
  method: z.enum(["card", "connect_wallet"]).nullable(),
  pairing: z.enum(["exact", "count"]).nullable(),
  provisional: z.boolean(),
  supportRef: z.string(),
  steps: z.object({
    submitted: stepView,
    received: stepView,
    sent: stepView,
    available: stepView,
  }),
  current: FunPaymentStepSchema,
  // A state a newer server added reads as delayed rather than failing the whole response.
  state: z
    .string()
    .transform((state): FunPaymentState => (isKnownState(state) ? state : "delayed")),
  retraction: z
    .object({
      of: z.union([FunPaymentStepSchema, z.literal("failed")]),
      at: z.string(),
      source: z.enum(["fun", "base", "near", "decibel"]),
    })
    .nullable(),
  awaitingBaseFinality: z.boolean(),
  startedAt: z.string(),
  updatedAt: z.string(),
  deadlineAt: z.string().nullable(),
  estimateMs: z.number().nullable(),
  amount: z.object({
    requestedUsd: z.string().nullable(),
    paidOutUsdc: z.string().nullable(),
  }),
  links: z.object({
    origin: z.object({ chainId: z.string(), hash: z.string() }).nullable(),
    payout: z.object({ hash: z.string() }).nullable(),
    delivery: z.object({ hash: z.string() }).nullable(),
  }),
  sentBy: z.enum(["payout_tx", "amount_fallback"]).nullable(),
  delivery: FunPaymentDeliverySchema.nullable(),
  isDeliveryOwner: z.boolean(),
  funRefundState: z.string().nullable(),
  nextAction: z.enum(["wait", "contact_support", "confirm_deposit", "dismiss"]),
  dismissible: z.boolean(),
  version: z.string(),
}) satisfies z.ZodType<FunPaymentItem>;

export const FunPaymentActivitySchema = z.object({
  accountAddress: z.string(),
  items: z.array(FunPaymentItemSchema),
  anyOpen: z.boolean(),
  serverPolling: z.boolean(),
  polledAt: z.string().nullable(),
  baseReadAsOf: z.string().nullable(),
  claimedDeliveryIds: z.array(z.string()),
}) satisfies z.ZodType<FunActivityResponse>;

export const FunPaymentLookupSchema = z.union([
  z.object({ accountAddress: z.string(), found: z.literal(true), item: FunPaymentItemSchema }),
  z.object({ accountAddress: z.string(), found: z.literal(false) }),
]) satisfies z.ZodType<FunLookupResponse>;

export const FunPaymentStartSchema = z.object({
  accountAddress: z.string(),
  paymentId: z.string().nullable(),
  originAssetId: z.string(),
  chain: z.string(),
  blockchain: z.string(),
  depositAddress: z.string(),
  depositMemo: z.string().nullable(),
}) satisfies z.ZodType<FunPaymentStartResponse>;

export const FunPaymentObservedSchema = z.object({
  accountAddress: z.string(),
  item: FunPaymentItemSchema.nullable(),
}) satisfies z.ZodType<FunPaymentObservedResponse>;

export function isOpenFunPayment(item: FunPaymentItem): boolean {
  return !isFinalFunPaymentState(item.state);
}
